/**
 * Auto-scroll rule of a drag near a scroll container's edge (ui-redesign §3.2): within 32px of
 * an edge the container scrolls towards it, the speed growing linearly up to 480px/s. The speed
 * is applied by elapsed time, so it is frame-rate independent; a late frame (background tab,
 * long task) is capped so one frame can never jump the content.
 */

/** Pointer within this many pixels of a scroll container's edge auto-scrolls it (§3.2). */
export const AUTO_SCROLL_EDGE_PX = 32;
/** Auto-scroll speed cap in pixels per second (§3.2). */
export const AUTO_SCROLL_MAX_PX_PER_S = 480;
/** Longest elapsed time one frame may apply: a late frame scrolls at most this far ahead. */
export const AUTO_SCROLL_MAX_STEP_S = 0.1;

/**
 * Signed auto-scroll velocity (px/s) for a pointer at `position` between the container edges
 * `start` and `end` (top/bottom or left/right in viewport pixels). Negative scrolls backwards
 * (up or left). Zero outside the 32px edge bands; the full 480px/s on or beyond the edge.
 */
export function edgeScrollVelocity(position: number, start: number, end: number): number {
  const fromStart = position - start;
  const fromEnd = end - position;
  if (fromStart < AUTO_SCROLL_EDGE_PX && fromStart <= fromEnd) {
    return -AUTO_SCROLL_MAX_PX_PER_S * (1 - Math.max(fromStart, 0) / AUTO_SCROLL_EDGE_PX);
  }
  if (fromEnd < AUTO_SCROLL_EDGE_PX) {
    return AUTO_SCROLL_MAX_PX_PER_S * (1 - Math.max(fromEnd, 0) / AUTO_SCROLL_EDGE_PX);
  }
  return 0;
}

/** Pixels one frame scrolls: velocity × elapsed time, the time step capped (frame-rate free). */
export function scrollStepPx(velocity: number, elapsedMs: number): number {
  return velocity * Math.min(Math.max(elapsedMs, 0) / 1000, AUTO_SCROLL_MAX_STEP_S);
}
